import { Injectable, inject } from '@angular/core';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateService,
} from '@ngx-translate/core';
import { DOCUMENT } from '@angular/common';

export interface Language {
  code: 'en' | 'ur' | 'ar';
  name: string;
  flag: string;
}

const RTL_LANGS = ['ur', 'ar'];
const STORAGE_KEY = 'app_lang';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private translate = inject(TranslateService);
  private document = inject(DOCUMENT);

  languages: Language[] = [
    { code: 'en', name: 'English (US)', flag: 'images/united-states-of-america.png' },
    { code: 'ur', name: 'Urdu', flag: 'images/pakistan.png' },
    { code: 'ar', name: 'Arabic', flag: 'images/saudi-arabia.png' },
  ];

  init(): Language {
    const savedCode = (localStorage.getItem(STORAGE_KEY) as Language['code']) || 'en';
    const lang = this.languages.find((l) => l.code === savedCode) || this.languages[0];
    this.applyLanguage(lang);
    return lang;
  }

  setLanguage(lang: Language): void {
    this.applyLanguage(lang);
  }

  getCurrentLanguage(): Language {
    const savedCode = (localStorage.getItem(STORAGE_KEY) as Language['code']) || 'en';
    return this.languages.find((l) => l.code === savedCode) || this.languages[0];
  }

  private applyLanguage(lang: Language): void {
    this.translate.use(lang.code); // ✅ ye method v18 mein bhi same hai
    localStorage.setItem(STORAGE_KEY, lang.code);

    const isRtl = RTL_LANGS.includes(lang.code);
    this.document.documentElement.lang = lang.code;
    this.document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
  }
}

const DATA_PREFIX = 'DATA.';

/**
 * Backend values (picklist labels, statuses, setup names such as "Active" or "Income") are not
 * translation keys, so the translate pipe would show them as-is. This handler gives them one shared
 * dictionary: any text passed through `translate` that is not a key is looked up under `DATA.*`
 * in public/i18n/*.json (key = the English text upper-cased, e.g. "Mark-up on Loans" ->
 * DATA.MARK_UP_ON_LOANS), so the same value is translated the same way on every screen.
 * Values without a dictionary entry, and everything in English, are shown unchanged.
 */
@Injectable()
export class DataTranslationHandler implements MissingTranslationHandler {
  handle({ key, translateService }: MissingTranslationHandlerParams): string {
    if (typeof key !== 'string' || key.startsWith(DATA_PREFIX) || key.length > 80) return key;
    if (translateService.getCurrentLang() === 'en') return key;

    const normalized = key
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    if (!normalized) return key;

    const dataKey = DATA_PREFIX + normalized;
    const translated = translateService.instant(dataKey);

    return translated === dataKey ? key : translated;
  }
}
